'use client'

import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Camera, FileText, Keyboard, Mic, PlusIcon } from 'lucide-react'
import AddBasePhotoModal from './add-base-photo-modal'
import BasePhotoViewer from './base-photo-viewer'
import { getBasePhotosByRegionId, deleteBasePhoto, updateBasePhoto } from '@/features/base/actions/base-photos.actions'
import { BasePhoto } from '@/features/base/types/base-content'
import VideoThumbnail from '@/features/photos/components/video-thumbnail'
import { PdfThumbnail } from '@/features/photos/components/pdf-thumbnail'
import VideoPlayer from '@/features/photos/components/video-player'
import PhotoDescriptionIndicator from '@/features/photos/components/photo-description-indicator'
import CameraCaptureModal from '@/features/photos/components/camera-capture-modal'
import PostCapturePopup from '@/features/photos/components/post-capture-popup'
import VoiceRecordingModal from '@/features/photos/components/voice-recording-modal'
import TextInputModal from '@/features/photos/components/text-input-modal'
import { uploadBasePhotosDirect } from '@/features/base/lib/upload-base-photos-client'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

const HUB_PHOTOS_FIRST_UPLOAD_KEY = 'reliefhub_hub_photos_first_upload_seen'
const PHOTOS_SHARING_MESSAGE =
  "Thanks for sharing! By posting your photo or video, you're giving ReliefHub the green light to display it across the platform for all users to see and celebrate."
const CONFIDENTIAL_TAG = 'Confidential'

type BasePhotoTimelineTabProps = {
  regionId: number
}

interface TimelineDay {
  date: string
  displayDate: string
  photos: BasePhoto[]
}

export default function BasePhotoTimelineTab({ regionId }: BasePhotoTimelineTabProps) {
  const { session } = useSession()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [preSelectedFiles, setPreSelectedFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<BasePhoto | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<BasePhoto | null>(null)
  const [pendingViewerDescription, setPendingViewerDescription] = useState<string | undefined>(undefined)
  const [isViewerVoiceOpen, setIsViewerVoiceOpen] = useState(false)
  const [isViewerTextOpen, setIsViewerTextOpen] = useState(false)
  const [isViewerTranscribing, setIsViewerTranscribing] = useState(false)
  const [photos, setPhotos] = useState<BasePhoto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [photoTagFilter, setPhotoTagFilter] = useState<string | null>(null) // 'Confidential' to filter timeline

  const canShowFilter =
    session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

  /** Volunteer and TeamLeader may only edit/delete photos they uploaded. Admin and BaseLeader may edit any. */
  const canEditPhoto = (photo: BasePhoto) => {
    if (session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER) return true
    if (session?.role === UserRole.VOLUNTEER || session?.role === UserRole.TEAM_LEADER) {
      const uploaderId = photo.uploadedByUser?.id ?? photo.uploadedByUserId
      return uploaderId != null && uploaderId === session?.sub
    }
    return false
  }

  interface CapturedPhoto {
    file: File
    url: string
    thumbnailUrl: string | null
    description?: string | null
    takenAt: string
    tempId: string
    uploadedPhotoId?: number
  }

  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([])
  const [showPostCapturePopup, setShowPostCapturePopup] = useState(false)
  const [currentPhotoTempId, setCurrentPhotoTempId] = useState<string | null>(null)
  const [lastSavedThumbnailUrl, setLastSavedThumbnailUrl] = useState<string | null>(null)
  const [isVoiceRecordingOpen, setIsVoiceRecordingOpen] = useState(false)
  const [isTextInputOpen, setIsTextInputOpen] = useState(false)
  const [uploadingPhotos, setUploadingPhotos] = useState<Set<string>>(new Set())
  const [showThankYouPopup, setShowThankYouPopup] = useState(false)
  const isTextInputSavingRef = useRef(false)
  const isTextInputOpenRef = useRef(isTextInputOpen)
  const isVoiceRecordingOpenRef = useRef(isVoiceRecordingOpen)
  const currentTakenAtRef = useRef<string>(new Date().toISOString())

  useEffect(() => {
    isTextInputOpenRef.current = isTextInputOpen
  }, [isTextInputOpen])
  useEffect(() => {
    isVoiceRecordingOpenRef.current = isVoiceRecordingOpen
  }, [isVoiceRecordingOpen])

  useEffect(() => {
    let isCancelled = false
    const canShowFilter =
      session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
    const fetchData = async () => {
      try {
        if (!isCancelled) setIsLoading(true)
        if (!isCancelled) setError(null)
        const result = await getBasePhotosByRegionId(regionId, {
          ...(canShowFilter && photoTagFilter && { tags: [photoTagFilter] }),
        })
        if (!isCancelled) {
          if (result.success && result.data) {
            // Exclude photos used by reports (e.g. Workscope Status) from the timeline
            const timelinePhotos = result.data.filter(
              (p) => (p.componentName ?? '').trim() !== 'Workscope Status'
            )
            setPhotos(timelinePhotos)
          } else {
            setError('message' in result ? result.message : 'Failed to fetch base photos')
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error')
        }
      } finally {
        if (!isCancelled) setIsLoading(false)
      }
    }
    fetchData()
    return () => { isCancelled = true }
  }, [regionId, refreshKey, photoTagFilter, session?.role])

  const handleUploadSuccess = () => {
    setRefreshKey((prev) => prev + 1)
    setPreSelectedFiles([])
    // Show thank-you popup on first upload (after modal closes)
    if (typeof window !== 'undefined' && !window.localStorage.getItem(HUB_PHOTOS_FIRST_UPLOAD_KEY)) {
      window.localStorage.setItem(HUB_PHOTOS_FIRST_UPLOAD_KEY, '1')
      setTimeout(() => setShowThankYouPopup(true), 150)
    }
  }

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files?.length) {
      setPreSelectedFiles(Array.from(files))
      setIsModalOpen(true)
    }
    e.target.value = ''
  }

  const getTimelineDays = (): TimelineDay[] => {
    const map = new Map<string, BasePhoto[]>()
    photos.forEach((p) => {
      const d = (p.takenAt || p.uploadedAt) as string
      const date = new Date(d).toDateString()
      if (!map.has(date)) map.set(date, [])
      map.get(date)!.push(p)
    })
    return Array.from(map.entries())
      .map(([date, phs]) => ({
        date,
        displayDate: formatDisplayDate(date),
        photos: phs.sort((a, b) => {
          const da = new Date((a.takenAt || a.uploadedAt) as string).getTime()
          const db = new Date((b.takenAt || b.uploadedAt) as string).getTime()
          return db - da
        }),
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }

  const formatDisplayDate = (dateStr: string) => {
    const d = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    if (d.toDateString() === today.toDateString()) return 'Today'
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  }

  const handlePhotoClick = (photo: BasePhoto) => {
    if (photo.mediaType === 'video') {
      setSelectedVideo(photo)
      setSelectedPhoto(null)
    } else {
      setSelectedPhoto(photo)
      setSelectedVideo(null)
    }
  }

  const closePhotoViewer = () => {
    setSelectedPhoto(null)
    setPendingViewerDescription(undefined)
    setIsViewerVoiceOpen(false)
    setIsViewerTextOpen(false)
    setIsViewerTranscribing(false)
  }

  const closeVideoPlayer = () => setSelectedVideo(null)

  const transcribeAudioBlob = async (audioBlob: Blob) => {
    const f = new File([audioBlob], 'recording.webm', { type: audioBlob.type || 'audio/webm' })
    const formData = new FormData()
    formData.append('audio', f)
    const res = await fetch('/api/ai/transcribe', { method: 'POST', body: formData, credentials: 'include' })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return { success: false as const, message: data?.message || 'Transcription failed' }
    if (data.success && data.data?.transcription != null) return { success: true as const, text: String(data.data.transcription) }
    return { success: false as const, message: data?.message || 'No transcription' }
  }

  const handleViewerVoiceSave = async (audioBlob: Blob) => {
    setIsViewerVoiceOpen(false)
    if (!audioBlob || audioBlob.size < 100) {
      alert('No audio recorded. Please try again.')
      return
    }
    if (!selectedPhoto) return
    setIsViewerTranscribing(true)
    try {
      const out = await transcribeAudioBlob(audioBlob)
      if (out.success) {
        const existing = (selectedPhoto.description ?? '').trim()
        const newDesc = existing ? `${existing} ${out.text}` : out.text
        setPendingViewerDescription(newDesc)
        const r = await updateBasePhoto(selectedPhoto.id, { description: newDesc })
        if (r.success) {
          setSelectedPhoto((p) => (p ? { ...p, description: newDesc } : null))
          setPendingViewerDescription(undefined)
          setRefreshKey((k) => k + 1)
        } else alert(`Failed to save: ${r.message}`)
      } else alert(`Transcription failed: ${out.message}`)
    } catch (e) {
      alert(`Error: ${e instanceof Error ? e.message : 'Unknown'}`)
    } finally {
      setIsViewerTranscribing(false)
    }
  }

  const handleViewerTextSave = async (text: string) => {
    setIsViewerTextOpen(false)
    if (!selectedPhoto) return
    setPendingViewerDescription(text)
    try {
      const r = await updateBasePhoto(selectedPhoto.id, { description: text })
      if (r.success) {
        setSelectedPhoto((p) => (p ? { ...p, description: text } : null))
        setPendingViewerDescription(undefined)
        setRefreshKey((k) => k + 1)
      } else alert(`Failed to save: ${r.message}`)
    } catch {
      alert('Error saving description')
    }
  }

  const handleDeletePhoto = async (photoId: number) => {
    if (!confirm('Delete this photo/video permanently?')) return
    try {
      const r = await deleteBasePhoto(photoId)
      if (r.success) {
        setSelectedPhoto(null)
        setSelectedVideo(null)
        setPhotos((prev) => prev.filter((p) => p.id !== photoId))
      } else alert(`Failed to delete: ${r.message}`)
    } catch {
      alert('Error deleting')
    }
  }

  const handleCameraFABClick = () => setIsCameraOpen(true)

  const uploadPhotoImmediately = async (
    tempId: string,
    description?: string | null,
    override?: CapturedPhoto,
    takenAt?: string | null
  ): Promise<number | undefined> => {
    const photo = override ?? capturedPhotos.find((p) => p.tempId === tempId)
    const taken = takenAt ?? photo?.takenAt ?? override?.takenAt
    if (!photo) return undefined
    if (uploadingPhotos.has(tempId)) return undefined
    setUploadingPhotos((s) => new Set(s).add(tempId))
    try {
      const result = await uploadBasePhotosDirect(regionId, [photo.file], {
        description: description ?? undefined,
        takenAt: taken,
        componentName: 'Base Photos',
      })
      if (!result.success || !result.data?.length) throw new Error('message' in result ? result.message : 'Upload failed')
      const uploaded = result.data[0]
      if (description || taken) {
        await updateBasePhoto(uploaded.id, {
          ...(description && { description }),
          ...(taken && { takenAt: taken }),
        })
      }
      if (photo.thumbnailUrl) setLastSavedThumbnailUrl(photo.thumbnailUrl)
      setCapturedPhotos((prev) => prev.filter((p) => p.tempId !== tempId))
      URL.revokeObjectURL(photo.url)
      // First-time thank-you popup for camera/capture uploads
      if (typeof window !== 'undefined' && !window.localStorage.getItem(HUB_PHOTOS_FIRST_UPLOAD_KEY)) {
        window.localStorage.setItem(HUB_PHOTOS_FIRST_UPLOAD_KEY, '1')
        setTimeout(() => setShowThankYouPopup(true), 150)
      }
      return uploaded.id
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Upload failed')
      return undefined
    } finally {
      setUploadingPhotos((s) => {
        const n = new Set(s)
        n.delete(tempId)
        return n
      })
    }
  }

  const handleCameraCapture = (file: File) => {
    if (file.size === 0) return
    const url = URL.createObjectURL(file)
    const tempId = `bp-${Date.now()}-${Math.random()}`
    const isVideo = file.type.startsWith('video/')
    if (!isVideo) setCurrentPhotoTempId(tempId)
    if (isVideo) {
      const video = document.createElement('video')
      video.src = url
      video.muted = true
      video.playsInline = true
      video.preload = 'metadata'
      let done = false
      const add = (thumbUrl: string | null) => {
        if (done) return
        done = true
        const cp: CapturedPhoto = { file, url, thumbnailUrl: thumbUrl, takenAt: new Date().toISOString(), tempId }
        setCapturedPhotos((p) => [...p, cp])
        uploadPhotoImmediately(tempId, null, cp).catch(console.error)
      }
      video.onloadedmetadata = () => { video.currentTime = Math.min(1, video.duration * 0.1) || 0.1 }
      video.onseeked = () => {
        const c = document.createElement('canvas')
        c.width = 48
        c.height = 48
        const ctx = c.getContext('2d')
        if (ctx && video.videoWidth) {
          const s = Math.max(48 / video.videoWidth, 48 / video.videoHeight)
          ctx.drawImage(video, 0, 0, video.videoWidth * s, video.videoHeight * s)
          c.toBlob((b) => add(b ? URL.createObjectURL(b) : null), 'image/jpeg', 0.8)
        } else add(null)
      }
      video.onerror = () => add(null)
      video.load()
    } else {
      const img = document.createElement('img')
      img.onerror = () => {
        const cp: CapturedPhoto = { file, url, thumbnailUrl: null, takenAt: new Date().toISOString(), tempId }
        setCapturedPhotos((p) => [...p, cp])
        setShowPostCapturePopup(true)
      }
      img.onload = () => {
        const c = document.createElement('canvas')
        c.width = 48
        c.height = 48
        const ctx = c.getContext('2d')
        let thumbUrl: string | null = null
        if (ctx) {
          const s = Math.max(48 / img.width, 48 / img.height)
          ctx.drawImage(img, 0, 0, img.width * s, img.height * s)
          c.toBlob((b) => {
            thumbUrl = b ? URL.createObjectURL(b) : null
            const cp: CapturedPhoto = { file, url, thumbnailUrl: thumbUrl, takenAt: new Date().toISOString(), tempId }
            setCapturedPhotos((p) => [...p, cp])
            setShowPostCapturePopup(true)
          }, 'image/jpeg', 0.8)
        } else {
          const cp: CapturedPhoto = { file, url, thumbnailUrl: null, takenAt: new Date().toISOString(), tempId }
          setCapturedPhotos((p) => [...p, cp])
          setShowPostCapturePopup(true)
        }
      }
      img.src = url
    }
  }

  const handlePostCaptureClose = (takenAt: string) => {
    setShowPostCapturePopup(false)
    if (currentPhotoTempId && !isVoiceRecordingOpenRef.current && !isTextInputOpenRef.current && !isTextInputSavingRef.current) {
      const tid = currentPhotoTempId
      setCurrentPhotoTempId(null)
      uploadPhotoImmediately(tid, null, undefined, takenAt).catch(console.error)
    }
  }

  const cleanupAllPhotos = async () => {
    if (capturedPhotos.length > 0) {
      await Promise.allSettled(capturedPhotos.map((p) => uploadPhotoImmediately(p.tempId, null, undefined, p.takenAt)))
    }
    capturedPhotos.forEach((p) => URL.revokeObjectURL(p.url))
    if (lastSavedThumbnailUrl) URL.revokeObjectURL(lastSavedThumbnailUrl)
    setCapturedPhotos([])
    setCurrentPhotoTempId(null)
    setShowPostCapturePopup(false)
    setLastSavedThumbnailUrl(null)
    setRefreshKey((k) => k + 1)
  }

  const handlePostCaptureVoiceRecord = (takenAt: string) => {
    currentTakenAtRef.current = takenAt
    setShowPostCapturePopup(false)
    setIsVoiceRecordingOpen(true)
  }

  const handlePostCaptureTextInput = (takenAt: string) => {
    currentTakenAtRef.current = takenAt
    setShowPostCapturePopup(false)
    setIsTextInputOpen(true)
  }

  const handleVoiceRecordingSave = async (audioBlob: Blob) => {
    setIsVoiceRecordingOpen(false)
    if (!currentPhotoTempId) return
    const tid = currentPhotoTempId
    setCurrentPhotoTempId(null)
    const uploadedId = await uploadPhotoImmediately(tid, null, undefined, currentTakenAtRef.current)
    if (!uploadedId) return
    try {
      const out = await transcribeAudioBlob(audioBlob)
      if (out.success) await updateBasePhoto(uploadedId, { description: out.text })
    } catch {}
    if (!isVoiceRecordingOpenRef.current && !isTextInputOpenRef.current) setRefreshKey((k) => k + 1)
  }

  const handleTextInputSave = async (text: string) => {
    isTextInputSavingRef.current = true
    setIsTextInputOpen(false)
    if (!currentPhotoTempId) {
      isTextInputSavingRef.current = false
      return
    }
    const tid = currentPhotoTempId
    setCurrentPhotoTempId(null)
    uploadPhotoImmediately(tid, text, undefined, currentTakenAtRef.current).finally(() => {
      isTextInputSavingRef.current = false
    })
  }

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedPhoto) setSelectedPhoto(null)
        if (selectedVideo) setSelectedVideo(null)
      }
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [selectedPhoto, selectedVideo])

  const timelineDays = getTimelineDays()

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex justify-end gap-2">
          <Button onClick={handleCameraFABClick} size="sm" className="bg-primary hover:bg-primary/90 text-white">
            <Camera className="w-3 h-3" /> Take
          </Button>
          <Button onClick={handleUploadButtonClick} size="sm">
            <PlusIcon className="w-3 h-3" /> Upload
          </Button>
        </div>
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
        <p className="text-xs text-muted-foreground rounded-md border border-border bg-muted/50 px-3 py-2 text-center">
          {PHOTOS_SHARING_MESSAGE}
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex justify-end gap-2">
          <Button onClick={handleCameraFABClick} size="sm" className="bg-primary hover:bg-primary/90 text-white">
            <Camera className="w-3 h-3" /> Take
          </Button>
          <Button onClick={handleUploadButtonClick} size="sm">
            <PlusIcon className="w-3 h-3" /> Upload
          </Button>
        </div>
        <div className="text-center py-12 text-red-500">{error}</div>
        <p className="text-xs text-muted-foreground rounded-md border border-border bg-muted/50 px-3 py-2 text-center">
          {PHOTOS_SHARING_MESSAGE}
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {canShowFilter && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Filter:</span>
              <Button
                type="button"
                variant={photoTagFilter === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPhotoTagFilter(null)}
              >
                All
              </Button>
              <Button
                type="button"
                variant={photoTagFilter === CONFIDENTIAL_TAG ? 'default' : 'outline'}
                size="sm"
                onClick={() =>
                  setPhotoTagFilter(photoTagFilter === CONFIDENTIAL_TAG ? null : CONFIDENTIAL_TAG)
                }
              >
                Confidential {photoTagFilter === CONFIDENTIAL_TAG ? 'On' : 'Off'}
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2 sm:justify-end">
            <Button onClick={handleCameraFABClick} size="sm" className="gap-2 bg-primary hover:bg-primary/90 text-white">
              <Camera className="w-3 h-3" /> Take
            </Button>
            <Button onClick={handleUploadButtonClick} size="sm" className="gap-2">
              <PlusIcon className="w-3 h-3" /> Upload
            </Button>
          </div>
        </div>
        <div>
          {timelineDays.length > 0 ? (
            <div className="space-y-8">
              {timelineDays.map((day) => (
                <div key={day.date} className="space-y-4">
                  <div className="flex items-center gap-4">
                    <h3 className="text-sm md:text-lg font-semibold">{day.displayDate}</h3>
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-sm text-muted-foreground">{day.photos.length} items</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {day.photos.map((photo) => {
                      const isVideo = (photo.mediaType ?? 'image') === 'video'
                      const isDoc = (photo.mediaType ?? 'image') === 'document'
                      return (
                        <div
                          key={photo.id}
                          className="relative group bg-card rounded border overflow-hidden cursor-pointer hover:shadow-md"
                          onClick={() => handlePhotoClick(photo)}
                        >
                          {isVideo ? (
                            <VideoThumbnail
                              photo={photo as unknown as import('@/features/photos/actions').Photo}
                              onClick={() => handlePhotoClick(photo)}
                              showDuration
                              showVideoIcon
                            />
                          ) : isDoc ? (
                            <>
                              <PdfThumbnail
                                downloadApiPath={`/api/base-photos/${photo.id}/download`}
                                className="object-cover rounded"
                                style={{ height: '200px', width: 'auto' }}
                                alt={photo.description || `PDF ${photo.id}`}
                              >
                                <div className="flex flex-col items-center justify-center rounded p-4 h-[200px] min-w-[140px] bg-muted">
                                  <FileText className="w-12 h-12 text-muted-foreground mb-2" />
                                  <span className="text-xs">PDF</span>
                                </div>
                              </PdfThumbnail>
                              <PhotoDescriptionIndicator hasDescription={!!photo.description} />
                            </>
                          ) : (
                            <>
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={photo.thumbnailUrl || photo.imageUrl}
                                alt={photo.description || `Photo ${photo.id}`}
                                className="object-cover rounded"
                                style={{ height: '200px', width: 'auto' }}
                              />
                              <PhotoDescriptionIndicator hasDescription={!!photo.description} />
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">No photos yet. Click Take or Upload to add.</div>
          )}
        </div>
        <p className="text-xs text-muted-foreground rounded-md border border-border bg-muted/50 px-3 py-2 text-center">
          {PHOTOS_SHARING_MESSAGE}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,application/pdf"
        multiple
        onChange={handleFileSelection}
        className="hidden"
      />

      <AddBasePhotoModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setPreSelectedFiles([]) }}
        regionId={regionId}
        onUploadSuccess={handleUploadSuccess}
        initialFiles={preSelectedFiles}
      />

      {selectedPhoto && (selectedPhoto.mediaType ?? 'image') !== 'video' && (
        <BasePhotoViewer
          photo={selectedPhoto}
          isOpen
          onClose={closePhotoViewer}
          description={selectedPhoto.description}
          pendingDescription={pendingViewerDescription}
          isTranscribing={isViewerTranscribing}
          canEdit={canEditPhoto(selectedPhoto)}
          canDownload={session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER}
          onDelete={handleDeletePhoto}
          onVoiceClick={() => setIsViewerVoiceOpen(true)}
          onKeyboardClick={() => setIsViewerTextOpen(true)}
          allPhotos={photos.filter((p) => (p.mediaType ?? 'image') !== 'video')}
          currentIndex={photos
            .filter((p) => (p.mediaType ?? 'image') !== 'video')
            .findIndex((p) => p.id === selectedPhoto.id)}
          onSelectIndex={(i) => {
            const list = photos.filter((p) => (p.mediaType ?? 'image') !== 'video')
            if (list[i]) setSelectedPhoto(list[i])
          }}
          hasConfidentialTag={(selectedPhoto.tags ?? []).includes(CONFIDENTIAL_TAG)}
          onToggleConfidential={
            canShowFilter
              ? async () => {
                  if (!selectedPhoto) return
                  const current = selectedPhoto.tags ?? []
                  const hasTag = current.includes(CONFIDENTIAL_TAG)
                  const nextTags = hasTag
                    ? current.filter((t) => t !== CONFIDENTIAL_TAG)
                    : [...current, CONFIDENTIAL_TAG]
                  const r = await updateBasePhoto(selectedPhoto.id, { tags: nextTags })
                  if (r.success) {
                    setSelectedPhoto((p) => (p ? { ...p, tags: nextTags } : null))
                    setRefreshKey((k) => k + 1)
                  } else {
                    alert(`Failed to update tag: ${r.message ?? 'Unknown error'}`)
                  }
                }
              : undefined
          }
        />
      )}

      {selectedPhoto && isViewerVoiceOpen && (
        <VoiceRecordingModal isOpen onClose={() => setIsViewerVoiceOpen(false)} onSave={handleViewerVoiceSave} autoStart imageUrl={selectedPhoto.imageUrl} />
      )}
      {selectedPhoto && isViewerTextOpen && (
        <TextInputModal
          isOpen={isViewerTextOpen}
          onClose={() => setIsViewerTextOpen(false)}
          onSave={handleViewerTextSave}
          initialValue={pendingViewerDescription ?? selectedPhoto.description ?? ''}
          imageUrl={selectedPhoto.imageUrl}
        />
      )}

      {selectedVideo && (selectedVideo.mediaType ?? 'image') === 'video' && (
        <VideoPlayer
          photo={selectedVideo as unknown as import('@/features/photos/actions').Photo}
          isOpen
          onClose={closeVideoPlayer}
          onDelete={() => {
            const deletedId = selectedVideo.id
            setSelectedVideo(null)
            setSelectedPhoto(null)
            setPhotos((prev) => prev.filter((p) => p.id !== deletedId))
          }}
          canDelete={canEditPhoto(selectedVideo)}
          canDownload={session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER}
          downloadApiPath={`/api/base-photos/${selectedVideo.id}/download`}
          deletePhotoFn={deleteBasePhoto}
        />
      )}

      <CameraCaptureModal
        isOpen={isCameraOpen}
        onClose={async () => { await cleanupAllPhotos(); setIsCameraOpen(false) }}
        onCapture={handleCameraCapture}
        lastCapturedThumbnail={capturedPhotos[capturedPhotos.length - 1]?.thumbnailUrl ?? lastSavedThumbnailUrl}
        hideDone={showPostCapturePopup || isVoiceRecordingOpen || isTextInputOpen}
      />

      {showPostCapturePopup && currentPhotoTempId && (() => {
        const cp = capturedPhotos.find((p) => p.tempId === currentPhotoTempId)
        return cp ? (
          <PostCapturePopup
            imageUrl={cp.url}
            initialTakenAt={cp.takenAt}
            onTakenAtChange={(t) => setCapturedPhotos((p) => p.map((x) => (x.tempId === currentPhotoTempId ? { ...x, takenAt: t } : x)))}
            onVoiceRecord={handlePostCaptureVoiceRecord}
            onTextInput={handlePostCaptureTextInput}
            onClose={handlePostCaptureClose}
          />
        ) : null
      })()}

      {isVoiceRecordingOpen && currentPhotoTempId && (() => {
        const cp = capturedPhotos.find((p) => p.tempId === currentPhotoTempId)
        return cp ? (
          <VoiceRecordingModal
            isOpen
            onClose={() => {
              setIsVoiceRecordingOpen(false)
              if (currentPhotoTempId) {
                const p = capturedPhotos.find((x) => x.tempId === currentPhotoTempId)
                handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
              }
            }}
            onSave={handleVoiceRecordingSave}
            autoStart
            imageUrl={cp.url}
          />
        ) : null
      })()}

      {isTextInputOpen && currentPhotoTempId && (() => {
        const cp = capturedPhotos.find((p) => p.tempId === currentPhotoTempId)
        return cp ? (
          <TextInputModal
            isOpen
            onClose={() => {
              if (!isTextInputSavingRef.current) {
                setIsTextInputOpen(false)
                const p = capturedPhotos.find((x) => x.tempId === currentPhotoTempId)
                handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
              }
            }}
            onSave={handleTextInputSave}
            onSavingChange={(s) => { isTextInputSavingRef.current = s }}
            imageUrl={cp.url}
          />
        ) : null
      })()}

      <Dialog open={showThankYouPopup} onOpenChange={setShowThankYouPopup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Thank you for sharing</DialogTitle>
            <DialogDescription>{PHOTOS_SHARING_MESSAGE}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowThankYouPopup(false)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
